import React, { useMemo } from 'react';
import { ComponentActionHandler } from './actionHandler';
import { Adaptor } from './adaptor';
import { onComponentEventAction } from '../shared/componentEventActionEmitter';
import { emitCustomEvent } from '../shared/customEventTrigger';
import { emitPage, PageEventListenerTypes } from '../shared/eventListener';
import { PositionHandler } from './PositionHandler';
import { generatePluginParams } from '../shared/actionParams';
import useOrientation from './useOrientation';

function ComponentItem({ meta, global, instance, ...injectProps }) {
  const {
    key,
    children,
    component: { component: Component },
    data,
    style,
    commonStyle,
    position,
    animateData,
    landscapeAnimateData,
    landscapePosition,
    landscapeStyleSwitch,
    landscapeAnimateSwitch,
    landscapeCommonStyleSwitch,
    landscapeWrapperStyleSwitch,
    slot,
  } = instance;

  const emit = (trigger, payload) => emitCustomEvent(instance, trigger, global, meta, payload);

  const on = (eventName, callback) => onComponentEventAction(instance, eventName, callback);

  const { isLandscape } = useOrientation();

  const useLandscapeStyle = useMemo(() => !!landscapeStyleSwitch && isLandscape, [
    isLandscape,
    landscapeStyleSwitch,
  ]);

  const useLandscapeAnimateData = useMemo(() => !!landscapeAnimateSwitch && isLandscape, [
    isLandscape,
    landscapeAnimateSwitch,
  ]);

  const useLandscapeCommonStyle = useMemo(() => !!landscapeCommonStyleSwitch && isLandscape, [
    isLandscape,
    landscapeCommonStyleSwitch,
  ]);

  const useLandscapeWrapperStyle = useMemo(() => !!landscapeWrapperStyleSwitch && isLandscape, [
    isLandscape,
    landscapeWrapperStyleSwitch,
  ]);

  // landscapeWrapperStyleSwitch 和 landscapeCommonStyleSwitch 都会影响到 landscapeFixedWrapperStyle
  const useLandscapeFixedWrapperStyle = useMemo(
    () => (!!landscapeWrapperStyleSwitch || !!landscapeCommonStyleSwitch) && isLandscape,
    [isLandscape, landscapeWrapperStyleSwitch],
  );

  const renderContent = (content) => {
    if (useLandscapeCommonStyle) {
      return landscapePosition ? (
        <PositionHandler
          positionStyle={landscapePosition}
          instance={instance}
          useLandscapeCommonStyle={useLandscapeCommonStyle}
          useLandscapeFixedWrapperStyle={useLandscapeFixedWrapperStyle}
        >
          {content}
        </PositionHandler>
      ) : (
        content
      );
    }
    return position ? (
      <PositionHandler
        positionStyle={position}
        instance={instance}
        useLandscapeFixedWrapperStyle={useLandscapeFixedWrapperStyle}
      >
        {content}
      </PositionHandler>
    ) : (
      content
    );
  };

  if (children) {
    const content = (
      <Component
        key={key}
        componentKey={key}
        data={data}
        meta={meta}
        global={global}
        style={style}
        commonStyle={commonStyle}
        animateData={animateData}
        landscapeAnimateData={landscapeAnimateData}
        emit={emit}
        on={on}
        slot={slot}
      >
        {children.map(childInstance => (
          <ComponentItem
            key={childInstance.key}
            instance={childInstance}
            meta={meta}
            global={global}
            slot={childInstance.slot}
          />
        ))}
      </Component>
    );

    return (
      <ComponentActionHandler
        instance={instance}
        meta={meta}
        global={global}
        useLandscapeWrapperStyle={useLandscapeWrapperStyle}
      >
        {renderContent(content)}
      </ComponentActionHandler>
    );
  }

  const content = (
    <ComponentActionHandler
      instance={instance}
      meta={meta}
      global={global}
      useLandscapeWrapperStyle={useLandscapeWrapperStyle}
    >
      <Adaptor
        instance={instance}
        injectProps={injectProps}
        meta={meta}
        global={global}
        useLandscapeStyle={useLandscapeStyle}
        useLandscapeCommonStyle={useLandscapeCommonStyle}
        useLandscapeAnimateData={useLandscapeAnimateData}
      />
    </ComponentActionHandler>
  );

  return renderContent(content);
}

export function AppRender({ componentInstances, pluginInstances, global, meta }) {
  const execPlugins = React.useCallback(() => {
    pluginInstances.forEach((plugin) => {
      const {
        plugin: { plugin: pluginFn },
      } = plugin;
      const params = generatePluginParams(plugin, global, meta);
      pluginFn(params);
    });
  }, []);

  React.useEffect(() => {
    execPlugins();
    emitPage(PageEventListenerTypes.INIT, { global, meta });
  }, []);

  return (
    <>
      {componentInstances.map(instance => (
        <ComponentItem key={instance.key} instance={instance} meta={meta} global={global} />
      ))}
    </>
  );
}
